import React from 'react';
import { Card, Tag, Typography, Avatar } from 'antd';
import { UserOutlined, EnvironmentOutlined, BankOutlined } from '@ant-design/icons';
import { Ambassador } from '../../types';
import { arteliaColors } from '../../theme';

const { Text, Title } = Typography;

interface AmbassadorCardProps {
  ambassador: Ambassador;
  highlighted?: boolean;
  onClick?: (ambassador: Ambassador) => void;
}

const AmbassadorCard: React.FC<AmbassadorCardProps> = ({
  ambassador,
  highlighted = false,
  onClick,
}) => {
  const isBooster = ambassador.role === 'Booster';
  
  // Generate initials for avatar
  const initials = ambassador.fullName
    .split(' ')
    .map(name => name.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const handleClick = () => {
    onClick?.(ambassador);
  };

  return (
    <Card
      size="small"
      hoverable={false}
      onClick={handleClick}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        backgroundColor: highlighted ? '#f0f8ff' : 'white',
        border: highlighted ? `2px solid ${arteliaColors.bluePrimary}` : '1px solid #f0f0f0',
        transition: 'border-color 0.2s ease',
      }}
      styles={{ body: { padding: '12px' } }}
    >
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: '32px 1fr 1fr',
        gridTemplateRows: 'auto auto',
        columnGap: '12px',
        rowGap: '6px',
        alignItems: 'center',
      }}>
        {/* Avatar - spans both rows */}
        <Avatar
          size={32}
          style={{
            backgroundColor: isBooster ? arteliaColors.greenDarker : arteliaColors.bluePrimary,
            fontWeight: 600,
            fontSize: '12px',
            gridRow: '1 / 3',
            gridColumn: '1',
          }}
          icon={!initials ? <UserOutlined /> : undefined}
        >
          {initials}
        </Avatar>

        {/* Row 1 Left: Full Name */}
        <div style={{ gridRow: '1', gridColumn: '2', minWidth: 0 }}>
          <Title 
            level={5} 
            style={{ 
              margin: 0, 
              fontSize: '14px', 
              fontWeight: 600,
              color: arteliaColors.bluePrimary,
              lineHeight: 1.2,
            }}
            ellipsis={{ tooltip: ambassador.fullName }}
          >
            {ambassador.fullName}
          </Title>
        </div>

        {/* Row 1 Right: Location */}
        <div style={{ 
          gridRow: '1', 
          gridColumn: '3', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '4px',
          justifyContent: 'flex-end',
          minWidth: 0,
        }}>
          <EnvironmentOutlined style={{ fontSize: '12px', color: '#999' }} />
          <Text 
            style={{ fontSize: '12px', color: '#666' }}
            ellipsis={{ tooltip: `${ambassador.city}, ${ambassador.country}` }}
          >
            {ambassador.city}, {ambassador.country}
          </Text>
        </div>

        {/* Row 2 Left: Activity */}
        <div style={{ gridRow: '2', gridColumn: '2', minWidth: 0 }}>
          <Text 
            style={{ 
              fontSize: '11px', 
              color: '#888',
              fontStyle: 'italic',
            }}
            ellipsis={{ tooltip: ambassador.activity }}
          >
            {ambassador.activity}
          </Text>
        </div>

        {/* Row 2 Right: Business Unit */}
        <div style={{ gridRow: '2', gridColumn: '3', minWidth: 0 }}>
          <Tag 
            size="small" 
            color="default"
            style={{ 
              fontSize: '10px',
              margin: 0,
              padding: '2px 6px',
              lineHeight: '16px',
              float: 'right',
            }}
          >
            {ambassador.businessUnit}
          </Tag>
        </div>
      </div>
    </Card>
  );
};

export default AmbassadorCard;